import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Add-to-Home-Screen prompt.
 * - On Chrome/Edge/Android: uses the native `beforeinstallprompt` event.
 * - On iOS Safari: shows a one-time instructional hint (no native API).
 * Hidden in Lovable previews and once dismissed for 30 days.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "forexmind_install_dismissed_at";
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

function isPreviewHost(): boolean {
  if (typeof window === "undefined") return true;
  const h = window.location.hostname;
  return (
    h.startsWith("id-preview--") ||
    h.startsWith("preview--") ||
    h.endsWith(".lovableproject.com") ||
    h.endsWith(".lovableproject-dev.com") ||
    h === "lovableproject.com" ||
    h === "lovableproject-dev.com"
  );
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // @ts-expect-error iOS Safari only
    window.navigator.standalone === true
  );
}

function wasDismissed(): boolean {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {}
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isPreviewHost() || isStandalone() || wasDismissed()) return;

    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS detection — Safari doesn't fire beforeinstallprompt.
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS) {
      const t = setTimeout(() => setShowIOS(true), 8000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    markDismissed();
  };

  const dismiss = () => {
    setDeferred(null);
    setShowIOS(false);
    markDismissed();
  };

  if (!deferred && !showIOS) return null;

  return (
    <div className="fixed inset-x-0 bottom-3 z-40 mx-auto w-[calc(100%-1.5rem)] max-w-md px-3 pointer-events-none">
      <div className="pointer-events-auto flex items-start gap-3 rounded-xl border border-primary/30 bg-card/95 p-3 shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--primary)_40%,transparent)] backdrop-blur">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Install ForexMind AI</p>
          {deferred ? (
            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              Get one-tap access from your home screen.
            </p>
          ) : (
            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              Tap <Share className="inline h-3 w-3 align-text-bottom" /> then{" "}
              <strong className="text-foreground">Add to Home Screen</strong>.
            </p>
          )}
          {deferred && (
            <Button
              size="sm"
              onClick={install}
              className="mt-2 h-7 bg-primary px-3 text-[11px] text-primary-foreground hover:bg-primary/90"
            >
              Install
            </Button>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="rounded-md p-1 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
